import { clean, tsMillis } from "./dispatchFormatters";
import {
  isAmbulance,
  isBike,
  isCart,
  isCourtesyUnit,
  isSupervisor,
  isSupport,
} from "./dispatchUnitUtils";

export function sortLiveCalls(a, b) {
  const rank = (call) => (call.status === "queued" ? 0 : 1);
  const rankDiff = rank(a) - rank(b);
  if (rankDiff !== 0) return rankDiff;
  return tsMillis(b.createdAt) - tsMillis(a.createdAt);
}

export function buildEmergencySuggestions(config, location, units, isAvailableFn) {
  const assignment = config?.responseAssignments?.[location];
  if (!assignment) return { autoSelectedIds: [], suggestedOnlyIds: [] };

 const normalizeUnitKey = (value = "") =>
  String(value).toUpperCase().replace(/[^A-Z0-9]/g, "");

const unitsById = new Map();

units.forEach((u) => {
  [u.id, u.name, u.unitName, u.displayName, u.label].forEach((key) => {
    const normalized = normalizeUnitKey(key);
    if (normalized) unitsById.set(normalized, u);
  });
});

  const pickFirst = (ids = [], predicate = () => true) => {
    for (const id of ids) {
     const unit = unitsById.get(normalizeUnitKey(id));
     
      if (!unit) continue;
      if (!isAvailableFn(unit)) continue;
      if (!predicate(unit)) continue;
      return unit.id;
    }
    return null;
  };

 const cartId = pickFirst(assignment.cartPriority || []);
  const bikeId = pickFirst(assignment.bikePriority || [], (u) => isBike(u));
  const supervisorId = pickFirst(assignment.supervisorPriority || [], (u) => isSupervisor(u));

  return {
    autoSelectedIds: [cartId, bikeId].filter(Boolean),
    suggestedOnlyIds: supervisorId ? [supervisorId] : [],
  };
}

/**
 * Courtesy suggestions now allow already-active transport units
 */
export function buildCourtesySuggestions(units, isAvailableFn, byPreferredUnitOrder) {
  const firstTransport = [...units]
    .filter(
      (u) =>
        isCourtesyUnit(u) &&
        !isBike(u) &&
        !isAmbulance(u) &&
        !String(`${u?.name || ""} ${u?.type || ""} ${u?.category || ""}`)
          .toLowerCase()
          .includes("stretcher")
    )
    .sort(byPreferredUnitOrder)[0];

  return {
    autoSelectedIds: firstTransport ? [firstTransport.id] : [],
    suggestedOnlyIds: [],
  };
}

export function getSuggestedData({
  config,
  location,
  callType,
  units,
  isAvailableFn,
  byPreferredUnitOrder,
}) {
  if (!location) return { autoSelectedIds: [], suggestedOnlyIds: [] };
  if (callType === "courtesyTransport") {
    return buildCourtesySuggestions(units, isAvailableFn, byPreferredUnitOrder);
  }
  return buildEmergencySuggestions(config, location, units, isAvailableFn);
}

export function nextTimelinePatch(unitId, nextStatus, serverTimestamp) {
  const base = `unitTimelines.${unitId}`;
  switch (nextStatus) {
    case "DISP":
      return { [`${base}.dispatchedAt`]: serverTimestamp() };
    case "ON_SCENE":
      return { [`${base}.onSceneAt`]: serverTimestamp() };
    case "TO_HQ":
      return { [`${base}.toHQAt`]: serverTimestamp() };
    case "ARRIVED_HQ":
      return { [`${base}.arrivedHQAt`]: serverTimestamp() };
    case "TRANSPORTING":
      return { [`${base}.transportingAt`]: serverTimestamp() };
    case "ARRIVED":
      return { [`${base}.arrivedAt`]: serverTimestamp() };
    case "CLEAR":
      return { [`${base}.clearedAt`]: serverTimestamp() };
    case "AVAIL":
      return { [`${base}.availableAt`]: serverTimestamp() };
    default:
      return {};
  }
}

export function getCourtesyPhase(call) {
  if (!call.hq?.courtesyRequested && call.callType !== "courtesyTransport") return "none";

  if (call.callType === "courtesyTransport" && call.status === "queued") return "queued";

  if (call.hq?.courtesyRequested && !call.hq?.courtesyAcknowledged) return "requested";

  if (call.hq?.courtesyQueued || call.status === "queued") return "queued";

  if (call.courtesyTransportUnitId) {
    const s = call.unitStatuses?.[call.courtesyTransportUnitId];

    if (s === "ON_SCENE") return "arrived";
    if (["TRANSPORTING", "ARRIVED", "AVAIL"].includes(s)) return "transporting";

    return "assigned";
  }

  if (call.hq?.courtesyAcknowledged) return "received";
  if (call.hq?.courtesyRequested) return "requested";

  return "none";
}

export function getCallHeaderStatus(call) {
  const pickup = clean(call.pickupLocation || call.hq?.courtesyPickupLocation || "Medical HQ");
  const dropoff = clean(call.dropoffLocation || call.hq?.courtesyDestination);
  const passengerCount = Number(call.hq?.courtesyPassengerCount || 0);
  const passengerText =
    passengerCount > 0 ? ` • ${passengerCount} Passenger${passengerCount === 1 ? "" : "s"}` : "";

  if (call.callType === "courtesyTransport") {
    if (call.status === "queued") return `Queued • ${pickup} → ${dropoff || "—"}`;
    if (call.status === "transporting") return dropoff ? `→ ${dropoff}` : "Transporting";
    if (call.status === "onScene") return `At Pickup • ${pickup}`;
    if (call.status === "dispatched") return `Assigned • ${pickup}`;
    return `${pickup} → ${dropoff || "—"}${passengerText}`;
  }

  const hospital = clean(call.hospitalTransport?.destinationHospital);
  const courtesyPhase = getCourtesyPhase(call);

 if (call.hospitalTransport?.requested) {
  const ambulanceId =
    call.hospitalTransport?.ambulanceUnitId || call.ambulanceUnitId;

  const ambulanceStatus = ambulanceId
    ? call.unitStatuses?.[ambulanceId]
    : Object.values(call.unitStatuses || {})[0];

  if (call.hospitalTransport?.transportingAt || ambulanceStatus === "ARRIVED") {
    return hospital ? `Transporting to ${hospital}` : "Transporting to Hospital";
  }

  if (ambulanceStatus === "ON_SCENE") {
    return hospital ? `Ambulance On Scene • ${hospital}` : "Ambulance On Scene";
  }

  if (ambulanceStatus === "DISP") {
    return hospital ? `Ambulance Responding • ${hospital}` : "Ambulance Responding";
  }

  if (ambulanceId) {
    return hospital ? `Ambulance Assigned • ${hospital}` : "Ambulance Assigned";
  }

  return "Awaiting Ambulance";
}

  if (courtesyPhase === "requested") return "Requested";
  if (courtesyPhase === "received") return "Request Received";
  if (courtesyPhase === "queued") return "Request in Queue";
  if (courtesyPhase === "assigned") return "Awaiting Courtesy Unit";
  if (courtesyPhase === "arrived") return "Arrived";
  if (courtesyPhase === "transporting") return dropoff ? `Transporting to ${dropoff}` : "Courtesy Transporting";

  switch (call.status) {
    case "queued":
      return "Queued";
    case "dispatched":
      return "Dispatched";
    case "onScene":
      return "Units On Scene";
    case "transporting":
      return "Transporting to HQ";
    case "arrivedHQ":
      return "At HQ";
    default:
      return clean(call.status) || "Active";
  }
}

export function getCallCardStyle(call, nowMs) {
  const flashOn = Math.floor(nowMs / 300) % 2 === 0;

  if (call.callType === "courtesyTransport") {
    return {
      borderColor: "#facc15",
      bgColor: "#78350f",
      statusColor: "#fef3c7",
    };
  }

  if (call.hospitalTransport?.requested) {
    return {
      borderColor: "#ef4444",
      bgColor:
        call.hospitalTransport?.acknowledged === false
          ? flashOn
            ? "#dc2626"
            : "#7f1d1d"
          : "#7f1d1d",
      statusColor: "#fee2e2",
    };
  }

  if (call.hq?.courtesyRequested) {
    return {
      borderColor: "#facc15",
      bgColor:
        call.hq?.courtesyAcknowledged === false
          ? flashOn
            ? "#ca8a04"
            : "#713f12"
          : "#713f12",
      statusColor: "#fef3c7",
    };
  }

  if (call.status === "arrivedHQ") {
    return {
      borderColor: "#22c55e",
      bgColor: "#166534",
      statusColor: "#dcfce7",
    };
  }

  if (call.status === "transporting") {
    return {
      borderColor: "#3b82f6",
      bgColor: "#1d4ed8",
      statusColor: "#dbeafe",
    };
  }

  if (call.status === "onScene" || call.status === "dispatched") {
    return {
      borderColor: "#f97316",
      bgColor: "#9a3412",
      statusColor: "#ffedd5",
    };
  }

  if (call.status === "queued") {
    return {
      borderColor: "#f59e0b",
      bgColor: "#92400e",
      statusColor: "#ffedd5",
    };
  }

  return {
    borderColor: "#64748b",
    bgColor: "#334155",
    statusColor: "#f8fafc",
  };
}

export function shouldShowDispatchControls(call) {
  if (call.callType === "courtesyTransport") return true;
  if (call.status === "arrivedHQ") {
    return !!call.hospitalTransport?.requested || !!call.hq?.courtesyRequested;
  }
  return true;
}

export function getHQSummary(call) {
  const hq = call.hq || {};
  return {
    patientName: clean(hq.firstName),
    bed: clean(hq.bedNumber),
    complaint: clean(hq.chiefComplaint) || clean(call.nature),
  };
}

function isFieldSupportLikeCart(unit) {
  return isCart(unit) || isSupervisor(unit) || isSupport(unit);
}

/**
 * KEY CHANGE:
 * - Courtesy transport units are NOT forced to AVAIL when arriving.
 * - They stay in flow so they can continue other linked trips.
 */
export function getNextUnitActions(unit, call, currentStatus) {
  const status = String(currentStatus || "").trim().toUpperCase();

  const isCourtesyFlow = call.callType === "courtesyTransport";

  const isHospitalAmbulanceFlow =
    isAmbulance(unit) ||
    !!call?.hospitalTransport?.requested ||
    !!call?.hospitalTransport?.ambulanceUnitId ||
    !!call?.ambulanceUnitId;

  if (isHospitalAmbulanceFlow) {
    if (status === "DISP") return ["ON_SCENE", "AVAIL"];
    if (status === "ON_SCENE") return ["TRANSPORTING", "AVAIL"];
    if (status === "TRANSPORTING") return ["AVAIL"];
    return [];
  }

  if (isBike(unit)) {
    if (status === "DISP") return ["ON_SCENE", "CLEAR"];
    if (status === "ON_SCENE") return ["CLEAR"];
    return [];
  }

  if (
  isFieldSupportLikeCart(unit) ||
  String(unit?.name || "").trim().toUpperCase() === "CART 5"
) {
    if (isCourtesyFlow) {
      if (status === "DISP") return ["ON_SCENE", "AVAIL"];
      if (status === "ON_SCENE") return ["TRANSPORTING", "AVAIL"];
      if (status === "TRANSPORTING") return ["ARRIVED", "AVAIL"];
      if (status === "ARRIVED") return ["CLEAR", "AVAIL"];
      return [];
    }

    if (status === "DISP") return ["ON_SCENE", "CLEAR"];
    if (status === "ON_SCENE") return ["TO_HQ", "CLEAR"];
    if (status === "TO_HQ") return ["ARRIVED_HQ", "CLEAR"];
    if (status === "ARRIVED_HQ") return ["CLEAR"];
    return [];
  }

  if (isCourtesyFlow) {
    if (status === "DISP") return ["ON_SCENE", "AVAIL"];
    if (status === "ON_SCENE") return ["TRANSPORTING", "AVAIL"];
    if (status === "TRANSPORTING") return ["ARRIVED", "AVAIL"];
    if (status === "ARRIVED") return ["CLEAR", "AVAIL"];
    return [];
  }

  return [];
}